import rawCatalog from './parts.v1.json'
import generatedPingPongParts from './pingpong-parts.generated.json'
import compoundPartLayouts from './compound-part-layouts.json'
import { PartCatalogSchema, type PartDefinition } from './part-definition'

const stlAssetUrls = import.meta.glob('../../PingPongParts/**/*.stl', {
  eager: true,
  import: 'default',
  query: '?url',
}) as Record<string, string>

// macOS stores Korean CAD paths in decomposed Unicode form, while production
// bundling can compose the JSON source path. Normalize both sides so the
// production catalog keeps the original STL instead of using a plain box.
const normalizedStlAssetUrls = new Map(
  Object.entries(stlAssetUrls).map(([path, url]) => [path.normalize('NFC'), url]),
)

function getStlAssetUrl(sourcePath: string) {
  const assetPath = `../../${sourcePath}`
  return stlAssetUrls[assetPath] ?? normalizedStlAssetUrls.get(assetPath.normalize('NFC'))
}

type SourceModelTransform = {
  position: [number, number, number]
  rotationQuaternion: [number, number, number, number]
}

const sourceCompoundLayouts = compoundPartLayouts as unknown as Record<string, {
  components: Record<string, SourceModelTransform & { role: string }>
}>

function getSourceModelTransforms(part: (typeof generatedPingPongParts.parts)[number]) {
  const layout = sourceCompoundLayouts[part.code]
  if (!layout) return undefined
  return part.stlFiles.map((sourcePath) => {
    const filename = sourcePath.split('/').at(-1) ?? ''
    const transform = layout.components[filename]
    if (!transform) throw new Error(`Missing ${part.code} compound transform for ${filename}`)
    return {
      position: transform.position,
      rotationQuaternion: transform.rotationQuaternion,
    }
  })
}

const familyColors: Record<string, string> = {
  'LINK-C1': '#e9a321',
  'LINK-C2': '#db8f24',
  'LINK-C3': '#ca7725',
  'LINK-C4': '#ba6728',
  'LINK-ET': '#4ca3ad',
  'LINK-MD': '#6376a8',
  'LINK-SM': '#8b69af',
  'LINK-WL': '#35445d',
  CUBE: '#30353c',
}

function getSourceDimensions(part: (typeof generatedPingPongParts.parts)[number]) {
  if (!part.dimensionsMm) {
    return (part.family === 'CUBE' ? [0.05, 0.05, 0.05] : [0.1, 0.012, 0.012]) as [number, number, number]
  }
  const dimensions = part.family === 'CUBE'
    ? part.dimensionsMm
    : [part.dimensionsMm[0], part.dimensionsMm[2], part.dimensionsMm[1]]
  return dimensions.map((value) => value / 1000) as [number, number, number]
}

const linkConnectors = [
  {
    id: 'end-a',
    type: 'link-end' as const,
    position: [-0.04, 0, 0] as [number, number, number],
    normal: [-1, 0, 0] as [number, number, number],
    up: [0, 1, 0] as [number, number, number],
    allowedTypes: ['g-cube-face', 'link-end'],
    rotationSteps: 4,
  },
  {
    id: 'end-b',
    type: 'link-end' as const,
    position: [0.04, 0, 0] as [number, number, number],
    normal: [1, 0, 0] as [number, number, number],
    up: [0, 1, 0] as [number, number, number],
    allowedTypes: ['g-cube-face', 'link-end'],
    rotationSteps: 4,
  },
]

const cubeConnectors = [
  ['cross-female-right', [0.018, 0, 0], [1, 0, 0], [0, 1, 0]],
  ['cross-female-left', [-0.018, 0, 0], [-1, 0, 0], [0, 1, 0]],
  // The top face has the same ordinary cross socket as four other faces.
  ['cross-female-top', [0, 0.01735, 0.0004], [0, 1, 0], [0, 0, -1]],
  ['cross-female-back', [0, 0, 0.0184], [0, 0, 1], [0, 1, 0]],
  // The only motor socket is the thick cross inside the circular housing.
  // CUBE.stl places it at raw +Z; the CAD-to-Babylon handedness conversion
  // reflects that source axis to scene -Z.
  ['motor-female-front', [0, -0.00065, -0.0176], [0, 0, -1], [0, 1, 0]],
  ['cross-female-bottom', [0, -0.01895, 0], [0, -1, 0], [0, 0, 1]],
].map(([id, position, normal, up]) => ({
  id: id as string,
  type: id === 'motor-female-front' ? 'motor-female' as const : 'cross-female' as const,
  position: position as [number, number, number],
  normal: normal as [number, number, number],
  up: up as [number, number, number],
  allowedTypes: id === 'motor-female-front' ? ['motor-male'] : ['cross-male'],
  rotationSteps: 4,
}))

const c102MotorMount = {
  id: 'motor-male',
  type: 'motor-male' as const,
  // C1-02's male cross runs from raw Z=9.5 to 13.5 mm. The centered STL
  // origin is Z=6.75 mm, so this anchor is exactly at the key's body seat.
  position: [0, 0.00275, 0] as [number, number, number],
  normal: [0, 1, 0] as [number, number, number],
  up: [0, 0, 1] as [number, number, number],
  allowedTypes: ['motor-female'],
  rotationSteps: 4,
}

const c213CrossMounts = [
  {
    id: 'cross-male-a',
    type: 'cross-male' as const,
    // The assembled cube centers are 39 mm apart. Their rear cross seats
    // leave a 2.2 mm gap, so the two C2-13 anchors sit 1.1 mm off-center.
    position: [0, 0, -0.0011] as [number, number, number],
    normal: [0, 0, -1] as [number, number, number],
    up: [0, 1, 0] as [number, number, number],
    allowedTypes: ['cross-female'],
    rotationSteps: 4,
  },
  {
    id: 'cross-male-b',
    type: 'cross-male' as const,
    position: [0, 0, 0.0011] as [number, number, number],
    normal: [0, 0, 1] as [number, number, number],
    up: [0, 1, 0] as [number, number, number],
    allowedTypes: ['cross-female'],
    rotationSteps: 4,
  },
]

const c302CrossMounts = [
  {
    id: 'cross-male-pair-left',
    type: 'cross-male' as const,
    position: [-0.0195, 0.0015, 0] as [number, number, number],
    normal: [0, 1, 0] as [number, number, number],
    up: [0, 0, 1] as [number, number, number],
    allowedTypes: ['cross-female'],
    rotationSteps: 4,
  },
  {
    id: 'cross-male-pair-right',
    type: 'cross-male' as const,
    position: [0.0195, 0.0015, 0] as [number, number, number],
    normal: [0, 1, 0] as [number, number, number],
    up: [0, 0, 1] as [number, number, number],
    allowedTypes: ['cross-female'],
    rotationSteps: 4,
  },
  {
    id: 'cross-male-single',
    type: 'cross-male' as const,
    position: [0, -0.0015, 0] as [number, number, number],
    normal: [0, -1, 0] as [number, number, number],
    up: [0, 0, 1] as [number, number, number],
    allowedTypes: ['cross-female'],
    rotationSteps: 4,
  },
]

const wl04MotorMount = {
  id: 'motor-male-wheel',
  type: 'motor-male' as const,
  position: [-0.001, 0, 0] as [number, number, number],
  normal: [-1, 0, 0] as [number, number, number],
  up: [0, 1, 0] as [number, number, number],
  allowedTypes: ['motor-female'],
  rotationSteps: 4,
}

const md01GearboxMounts = [
  {
    id: 'motor-male-input',
    type: 'motor-male' as const,
    // MD-01 C is the cube-side input gear. The STEP assembly centers the
    // input/output shaft at X=17.9724, Y=18 mm; C's seated face is Z=-3.9552.
    position: [0, 0.0035985, 0] as [number, number, number],
    normal: [0, -1, 0] as [number, number, number],
    up: [0, 0, 1] as [number, number, number],
    allowedTypes: ['motor-female'],
    rotationSteps: 4,
  },
  {
    id: 'motor-female-output',
    type: 'motor-female' as const,
    // E is the wheel-side output gear. Its exposed face is Z=10.7448 in the
    // STEP layout, 14.7 mm away from C along the shared shaft axis.
    position: [0, 0.0182985, 0] as [number, number, number],
    normal: [0, 1, 0] as [number, number, number],
    up: [0, 0, 1] as [number, number, number],
    allowedTypes: ['motor-male'],
    rotationSteps: 4,
  },
]

const c103Mounts = [
  {
    id: 'motor-male-fork',
    type: 'motor-male' as const,
    // C1-03's source Z geometry is reflected by the instance factory so the
    // visible motor disc and this left-side connector share local +Y.
    position: [0.024435, 0.018, -0.00711] as [number, number, number],
    normal: [0, -1, 0] as [number, number, number],
    // Keeps the fork opening across the cube while the scoop stays horizontal.
    up: [0, 0, -1] as [number, number, number],
    allowedTypes: ['motor-female'],
    rotationSteps: 4,
  },
  {
    id: 'cross-male-fork',
    type: 'cross-male' as const,
    position: [0.024435, -0.018, -0.00711] as [number, number, number],
    normal: [0, 1, 0] as [number, number, number],
    up: [0, 0, -1] as [number, number, number],
    allowedTypes: ['cross-female'],
    rotationSteps: 4,
  },
]

const c119CrossMount = {
  id: 'cross-male-foot',
  type: 'cross-male' as const,
  // C1-19 is a single cross peg over a round foot. In the source STL the
  // body is centered at raw Z=-3.3 mm and the peg seat is raw Z=0.5 mm.
  // LINK parts map source +Z to scene +Y.
  position: [0, 0.0038, 0] as [number, number, number],
  normal: [0, 1, 0] as [number, number, number],
  up: [0, 0, 1] as [number, number, number],
  allowedTypes: ['cross-female'],
  rotationSteps: 4,
}

function connectorsForSourcePart(part: (typeof generatedPingPongParts.parts)[number]) {
  if (part.family === 'CUBE') return cubeConnectors
  if (part.code === 'C1-02') return [c102MotorMount]
  if (part.code === 'C1-03') return c103Mounts
  if (part.code === 'C1-19') return [c119CrossMount]
  if (part.code === 'C2-13') return c213CrossMounts
  if (part.code === 'C3-02') return c302CrossMounts
  if (part.code === 'MD-01') return md01GearboxMounts
  if (part.code === 'WL-04') return [wl04MotorMount]
  return linkConnectors
}

const sourceParts: PartDefinition[] = generatedPingPongParts.parts.map((part) => ({
  id: `part-${part.id}`,
  name: part.family === 'CUBE' ? '핑퐁 큐브 바디' : part.code,
  subtitle: part.family,
  category: part.family === 'CUBE' ? 'g-cube' : 'link',
  series: part.family,
  kind: part.family === 'CUBE' ? 'frame' : 'link',
  color: familyColors[part.family] ?? '#68758c',
  modelUrl: null,
  modelUrls: part.stlFiles.map(getStlAssetUrl).filter(Boolean),
  modelTransforms: getSourceModelTransforms(part),
  modelUnit: 'millimeter',
  modelUpAxis: part.family === 'CUBE' ? 'y' : 'z',
  thumbnailUrl: `/assets/part-thumbnails/part-${part.id}.png`,
  dimensions: getSourceDimensions(part),
  physics: {
    mass: part.family === 'CUBE' ? 0.05 : 0.024,
    friction: 0.7,
    restitution: 0.05,
    collider: {
      type: 'cuboid',
      size: getSourceDimensions(part).map((value) => value / 2) as [number, number, number],
    },
  },
  connectors: connectorsForSourcePart(part),
  specStatus: 'source-only',
  source: {
    family: part.family,
    code: part.code,
    stlFiles: part.stlFiles,
    stepFiles: part.stepFiles,
  },
}))

const catalog = PartCatalogSchema.parse({
  version: rawCatalog.version,
  parts: [...rawCatalog.parts, ...sourceParts],
})

export const parts = catalog.parts
export const partCatalogVersion = catalog.version

export function getPartById(partId: string): PartDefinition | undefined {
  return parts.find((part) => part.id === partId)
}

export const partCategories = [
  { id: 'pingpong', name: '핑퐁 큐브', series: 'CUBE' },
  { id: 'link-c', name: 'Link C', seriesPrefix: 'LINK-C' },
  { id: 'link-et', name: 'ET', series: 'LINK-ET' },
  { id: 'link-md', name: 'MD', series: 'LINK-MD' },
  { id: 'link-sm', name: 'SM', series: 'LINK-SM' },
  { id: 'link-wl', name: 'WL', series: 'LINK-WL' },
] as const
